import { formatPercent } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, AccountState } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import { AccountMarketplaceService, AccountSelectionService, AuthService, isReadOnlyAccess } from "@front/m19-services";
import { ConfirmPopupComponent, ConfirmPopupData, IButtonDirective, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import {
  ConfirmDspAutomationModalComponent,
  ConfirmDspAutomationModalData,
  dspActivationConfirmMessageKey,
} from "@m19-board/settings/account-settings/account-list/confirm-dsp-automation-modal.component";
import { finalize, Observable, switchMap } from "rxjs";

@Component({
  selector: "app-activate-dsp-automation-cta",
  imports: [IButtonDirective],
  template: `
    @if (canActivateDsp() && isAdminOrOwner() && !isReadOnly()) {
      <button iButton size="sm" (click)="activateDspBidder()" [loading]="loading()">
        {{ label() }}
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateDspAutomationCtaComponent {
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly accountMarketplace = input.required<AccountMarketplace>();
  readonly organization = input.required<OrganizationAccountGroups>();
  readonly isAdminOrOwner = input.required<boolean>();
  readonly label = input.required<string>();

  private readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly selectedOrganizationAccountMarketplaces = toSignal(
    this.accountSelectionService.selectedOrganizationAccountMarketplaces$,
  );

  protected readonly loading = signal(false);

  protected readonly canActivateDsp = computed(() => {
    const am = this.accountMarketplace();
    return (
      am.state === AccountState.BIDDER_ON &&
      am.dspAdvertiserId !== undefined &&
      am.dspAdvertiser?.bidderOrganizationId === undefined
    );
  });

  protected readonly isReadOnly = computed(() => isReadOnlyAccess(this.accountMarketplace().accessLevel));

  private readonly hasSeatOnM19 = computed(() =>
    AccountMarketplaceService.m19SeatProfileIds.includes(this.accountMarketplace().dspAdvertiser?.profileId ?? 0),
  );

  private readonly dspAdSpendFee = computed(() => {
    const plan = this.organization().organization.billingPlan;
    return this.hasSeatOnM19() ? (plan?.dspM19SeatAdSpendFee?.amount ?? 0) : (plan?.dspAdSpendFee?.amount ?? 0);
  });

  private readonly accountMarketplacesWithSameGlobalAdvertiser = computed<AccountMarketplace[] | undefined>(() => {
    const dspAdvertiser = this.accountMarketplace().dspAdvertiser;
    if (!dspAdvertiser) return undefined;

    if (!dspAdvertiser.isRegional) return [this.accountMarketplace()];

    return this.selectedOrganizationAccountMarketplaces()?.filter(
      (am) => am.dspAdvertiserId === dspAdvertiser.dspAdvertiserId,
    );
  });

  protected activateDspBidder(): void {
    if (!this.organization().canActivateDspBidder(this.accountMarketplace())) {
      this.toasterService.error("DSP bidder activation is not possible for this billing plan");
      return;
    }
    const am = this.accountMarketplace();
    this.confirmActivation()
      .pipe(
        switchMap(() => {
          this.loading.set(true);
          return this.accountMarketplaceService.activateDspBidder(am.dspAdvertiser!, am.resourceOrganizationId!);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: () => this.toasterService.success("DSP bidder successfully activated", "DSP bidder activation"),
        error: (error) => this.toasterService.error(error, "DSP bidder activation error"),
      });
  }

  private confirmActivation(): Observable<boolean> {
    const accountMarketplacesWithSameGlobalAdvertiser = this.accountMarketplacesWithSameGlobalAdvertiser();
    if (accountMarketplacesWithSameGlobalAdvertiser && accountMarketplacesWithSameGlobalAdvertiser.length > 1) {
      return this.modalService
        .openModal<ConfirmDspAutomationModalData, boolean>(ConfirmDspAutomationModalComponent, {
          modalTitle: this.translocoService.translate("account-setting.dsp_activation_confirm_title"),
          data: {
            accountMarketplace: this.accountMarketplace(),
            accountMarketplacesWithSameGlobalAdvertiser,
            adSpendFee: this.dspAdSpendFee(),
            onM19Seat: this.hasSeatOnM19(),
            activatingBidder: true,
          },
        })
        .afterClosed();
    }
    return this.modalService
      .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
        modalTitle: this.translocoService.translate("account-setting.dsp_activation_confirm_title"),
        data: {
          message: this.translocoService.translate(dspActivationConfirmMessageKey(this.hasSeatOnM19()), {
            dspAdSpendFee: formatPercent(this.dspAdSpendFee(), this.locale(), "1.0-2"),
          }),
          color: "main",
          confirmLabel: this.translocoService.translate("account-setting.dsp_activation_confirm_button_label"),
        },
      })
      .afterClosed();
  }
}
